import { BrowserModule } from '@angular/platform-browser';
import { NgModule, APP_INITIALIZER, ErrorHandler } from '@angular/core';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { HttpClientModule, HttpClient } from '@angular/common/http';
import {
  TranslateModule,
  TranslateLoader,
  TranslateService,
} from '@ngx-translate/core';
import Bugsnag from '@bugsnag/js';
import { BugsnagErrorHandler } from '@bugsnag/plugin-angular';
import { AppRoutingModule } from './app-routing.module';

import { AppComponent } from './app.component';
import { PageComponent } from './page/page.component';
import { SharedModule } from './shared/shared.module';

import { MasterDataService } from './core/services/master-data.service';
import { AppSettings } from './core/library/app-settings';
import { ApiService } from '@core/services/api.service';

import { UiModule } from '@ui/ui.module';
import { AuthGuard } from '@core/guards/auth.guard';
import { LicenceGuard } from '@core/guards/licence.guard';
import { AppPreloaderService } from '@core/services/app-preloader.service';
import { KMTTranslateLoader } from '@core/utils/translate-factory';
import {
  NgxGoogleAnalyticsModule,
  NgxGoogleAnalyticsRouterModule,
} from 'ngx-google-analytics';
import { ComplaintPrintComponent } from '@features/common/components/complaint-print/complaint-print.component';
import { FormsModule } from '@angular/forms';
import { EnumVerifyPipe } from '@ui/pipes/enum-verify.pipe';
import { ComplainAttachmentStreamComponent } from '@features/common/components/complain-attachment-stream/complain-attachment-stream.component';
import { DatePipe } from '@angular/common';
import { MatChipsModule } from '@angular/material/chips';
import { UFBootstrapService } from '@core/services/uf-bootstrap-service';

// configure Bugsnag asap
Bugsnag.start(AppSettings.BUGSNAG_ID);

// create a factory which will return the bugsnag error handler
export function errorHandlerFactory() {
  return new BugsnagErrorHandler();
}

@NgModule({
  declarations: [
    AppComponent,
    PageComponent,
    ComplaintPrintComponent,
    ComplainAttachmentStreamComponent,
    EnumVerifyPipe,
  ],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    SharedModule,
    UiModule,
    FormsModule,
    MatChipsModule,
    // app routing
    AppRoutingModule,
    HttpClientModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: TranslationLoaderFactory,
        deps: [HttpClient, AppPreloaderService],
      },
    }),
    NgxGoogleAnalyticsModule.forRoot(AppSettings.GA_TRACKING_ID),
    NgxGoogleAnalyticsRouterModule.forRoot(),
  ],
  exports: [TranslateModule],
  providers: [
    ApiService,
    MasterDataService,
    {
      provide: APP_INITIALIZER,
      useFactory: AppInitializerFactory,
      deps: [
        AppPreloaderService,
        TranslateService,
        UFBootstrapService,
        MasterDataService,
      ],
      multi: true,
    },
    { provide: ErrorHandler, useFactory: errorHandlerFactory },
    AuthGuard,
    LicenceGuard,
    DatePipe,
    UFBootstrapService,
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}

export function AppInitializerFactory(
  appPreloaderService: AppPreloaderService,
  translateService: TranslateService,
  ufBootstrapService: UFBootstrapService,
  configService: MasterDataService
): () => Promise<any> {
  return () =>
    appPreloaderService.updateConfig().then(() =>
      ufBootstrapService
        .bootstrapApplicationEnvironment()
        .then(() => configService.RefreshToken())
        .then(() => appPreloaderService.updateCurrentUser())
        .then(() =>
          Promise.all([
            appPreloaderService.updateCaches(),
            appPreloaderService.updateTranslations(translateService),
          ]).then(() => {
            AppSettings.IsRunningInSingularity =
              ufBootstrapService.isRunningInIframe() &&
              ufBootstrapService.isRunningInSingularity();
            return appPreloaderService.startSocketConnection();
          })
        )
    );
}

export function TranslationLoaderFactory(
  http: HttpClient,
  appPreloaderService: AppPreloaderService
): any {
  return new KMTTranslateLoader(
    http,
    appPreloaderService.initializingUser$,
    (staffInfo, langCode) =>
      AppSettings.URL_Masters +
      'OpenAPI/GetStaticUILabelsForWeb?PropertyID=' +
      staffInfo.PropertyId +
      '&LanguageCode=' +
      langCode
  );
}
